import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { Breadcrumb } from 'components/layout/Breadcrumb';
import { useGetBlueprintDetails } from 'features/blueprints';
import { AccordionPanel, LabelTag } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useSnackbar } from 'notistack';
import { Blueprint } from 'types';
import { canDeleteBlueprints, canUpdateBlueprints } from 'utils/authorization';
import { captureChartAsBlob } from 'utils/chartScreenshot';
import { DATE_FORMAT } from 'utils/constants';
import { representDate } from 'utils/formatters';
import { blueprintsRoutes } from '../Blueprints.routes';
import { BlueprintCalculationDetailsForm } from '../components/BlueprintCalculationDetailsForm';
import { BusinessCaseComparisonTable } from '../components/BusinessCaseComparisonTable';
import { DeleteBlueprintButton } from '../components/DeleteBlueprintButton';
import { DisclaimerBox } from '../components/DisclaimerBox';
import { MicrogridSetupBox } from '../components/MicrogridSetupBox';
import { PowerRequirementsBox } from '../components/PowerRequirementsBox';
import { ProjectPartnersBox } from '../components/ProjectPartnersBox';
import { ProposedSolutionBox } from '../components/ProposedSolutionBox';
import { ShareBlueprintModal } from '../components/ShareBlueprintModal';
import { getProjectTypeTranslationKey, getSetupDetails } from '../components/utils';

export function Component() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { blueprintDetails, isLoading, isError } = useGetBlueprintDetails({
    blueprintId: id!,
    enabled: !!id,
  });
  const theme = useTheme();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPreparingShare, setIsPreparingShare] = useState(false);
  const [chartScreenshot, setChartScreenshot] = useState<Blob | null>(null);

  const handleOpenShareModal = async () => {
    if (!blueprintDetails?.blueprintId) {
      return;
    }

    setIsPreparingShare(true);

    try {
      const screenshot = await captureChartAsBlob('[data-chart-id="power-profile"]', {
        scale: 2,
        quality: 0.95,
        backgroundColor: theme.palette.grey[50],
      });

      setChartScreenshot(screenshot);
      setIsShareModalOpen(true);
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.prepareFailed'), { variant: 'error' });
    } finally {
      setIsPreparingShare(false);
    }
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Box>{t('blueprints:details.error.loading')}</Box>;
  }

  if (!blueprintDetails) {
    return <Box>{t('blueprints:details.error.notFound')}</Box>;
  }

  return (
    <Box>
      <Breadcrumb routes={blueprintsRoutes} />
      <Box pt={0} pb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h1">{blueprintDetails.name}</Typography>
            <LabelTag label={t('entities:blueprint.nameSingular')} color={(theme) => theme.palette.badge5} />
          </Box>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" mb={3} gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
        <Typography color="grey.800" variant="subtitle1">
          {blueprintDetails.clientName}
          &nbsp;&nbsp;•&nbsp;&nbsp;{t(getProjectTypeTranslationKey(blueprintDetails.projectTypeId))}
          &nbsp;&nbsp;•&nbsp;&nbsp;{representDate(blueprintDetails.tsStart, DATE_FORMAT)}
          {blueprintDetails.tsEnd && <>&nbsp;&nbsp;/&nbsp;&nbsp;{representDate(blueprintDetails.tsEnd, DATE_FORMAT)}</>}
        </Typography>

        <Box gap={2} display="flex" flexDirection="row" justifyContent="flex-end">
          {canUpdateBlueprints(user?.role) ? (
            <Button data-testid="edit-solution-button" component={Link} to={`/solutions/${id}/edit?step=1`}>
              {t('components:form.edit')} {t('entities:blueprint.nameSingular')}
            </Button>
          ) : null}

          <Button variant="outlined" onClick={handleOpenShareModal} disabled={isPreparingShare}>
            {isPreparingShare ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
            {t('blueprints:details.shareButton')}
          </Button>

          {canDeleteBlueprints(user?.role) ? (
            <DeleteBlueprintButton blueprintId={blueprintDetails.blueprintId} blueprintName={blueprintDetails.name} />
          ) : null}
        </Box>
      </Box>

      {isDesktop ? (
        <DesktopLayout blueprintDetails={blueprintDetails} readOnly={!canUpdateBlueprints(user?.role)} />
      ) : (
        <MobileLayout blueprintDetails={blueprintDetails} readOnly={!canUpdateBlueprints(user?.role)} />
      )}

      <ShareBlueprintModal
        open={isShareModalOpen}
        onClose={handleCloseShareModal}
        blueprintName={blueprintDetails.name}
        blueprintId={blueprintDetails.blueprintId}
        chartScreenshot={chartScreenshot}
        blueprintDetails={blueprintDetails}
      />
    </Box>
  );
}

type LayoutProps = {
  blueprintDetails: Blueprint;
  readOnly: boolean;
};

const DesktopLayout = ({ blueprintDetails, readOnly }: LayoutProps) => {
  const setupDetails = getSetupDetails(blueprintDetails);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 3,
        mt: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <BusinessCaseComparisonTable blueprintDetails={blueprintDetails} />
        <ProposedSolutionBox blueprintDetails={blueprintDetails} readOnly={readOnly} />
        <PowerRequirementsBox blueprintDetails={blueprintDetails} readOnly={readOnly} />
        <DisclaimerBox />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ProjectPartnersBox blueprintDetails={blueprintDetails} />
        <BlueprintCalculationDetailsForm blueprintDetails={blueprintDetails} />
        <MicrogridSetupBox imageSvg={setupDetails?.imageSvg} setupType={setupDetails?.setupType} />
      </Box>
    </Box>
  );
};

const MobileLayout = ({ blueprintDetails, readOnly }: LayoutProps) => {
  const { t } = useTranslation();
  const setupDetails = getSetupDetails(blueprintDetails);

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ProjectPartnersBox blueprintDetails={blueprintDetails} />
      <BusinessCaseComparisonTable blueprintDetails={blueprintDetails} />
      <AccordionPanel
        contentStyles={{ padding: 0 }}
        divider
        title={t('blueprints:details.calculationDetails.title')}
        defaultExpanded={false}
        sx={{ borderRadius: '8px !important' }}
      >
        <BlueprintCalculationDetailsForm blueprintDetails={blueprintDetails} hideTitle={true} />
      </AccordionPanel>
      <MicrogridSetupBox imageSvg={setupDetails?.imageSvg} setupType={setupDetails?.setupType} />
      <ProposedSolutionBox blueprintDetails={blueprintDetails} readOnly={readOnly} />
      <PowerRequirementsBox blueprintDetails={blueprintDetails} readOnly={readOnly} />
      <DisclaimerBox />
    </Box>
  );
};

Component.displayName = 'BlueprintDetails';
